import React, { useMemo } from 'react';
import { ActivityIndicator, Text, View, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Chip from '@/components/General/Chip';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Toggle from '@/components/General/inputs/Toggle';
import MilestoneBadge from '@/components/Milestones/MilestoneBadge';
import { MILESTONE_SCOPES } from '@/config/milestones';
import { canEditMilestones } from '@/utils/milestonePermissions';

/**
 * Definition preview rows for one scope section of the organization
 * Milestones definitions drawer. Each card is a summary — name, metric, tier
 * count, Built-in + status chips, and the enable/disable toggle. Everything
 * else (edit, duplicate, archive, tier detail) lives on the milestone detail
 * page, reached by tapping the card.
 */
const MilestoneDefinitionList = ({ scope, definitions = [], catalog, loggedInUser, loading = false, pendingToggleID = null, onOpen, onToggleEnabled }) => {
    const editable = canEditMilestones(loggedInUser);

    const metricLabel = useMemo(() => {
        const labels = {};
        (catalog?.metrics ?? []).forEach(m => { labels[m.key] = m.label; });
        return labels;
    }, [catalog]);

    if (loading) {
        return (
            <View style={styles.loading}>
                <ActivityIndicator />
            </View>
        );
    }

    if (!definitions.length) {
        return (
            <EmptyListPlaceholder
                noLottie
                spacing={24}
                message={scope === MILESTONE_SCOPES.ORGANIZATION
                    ? 'No organization milestones yet. Built-in series or a new custom milestone will appear here.'
                    : 'No individual milestones yet. Create one to celebrate personal achievements.'}
            />
        );
    }

    return (
        <View style={styles.list}>
            {definitions.map(def => {
                const archived = !!def.deletedAt;
                const tierCount = (def.thresholds ?? []).length;

                return (
                    <Pressable
                        key={def.id}
                        testID={`milestone-definition-${def.id}`}
                        style={styles.row(archived)}
                        onPress={() => onOpen?.(def)}
                        accessibilityLabel={`${def.name} milestone`}
                    >
                        <MilestoneBadge
                            icon={def.badgeIcon}
                            colorToken={def.badgeColorToken}
                            threshold={null}
                            seriesName={def.name}
                            earned={def.enabled && !archived}
                            historical={archived}
                            size="sm"
                            showThreshold={false}
                        />
                        <View style={styles.rowBody}>
                            <Text style={styles.title(archived)} numberOfLines={1}>{def.name}</Text>
                            <Text style={styles.subtitle} numberOfLines={1}>
                                {metricLabel[def.metricKey] ?? def.metricKey}
                                {tierCount ? ` · ${tierCount} tier${tierCount === 1 ? '' : 's'}` : ' · No tiers'}
                            </Text>
                            <View style={styles.chipRow}>
                                {def.builtinKey && (
                                    <Chip colorVariant="admin" icon="star-outline" testID={`milestone-builtin-${def.id}`}>Built-in</Chip>
                                )}
                                {archived
                                    ? <Chip colorVariant="warning" icon="archive-outline">Archived</Chip>
                                    : <Chip colorVariant={def.enabled ? 'success' : 'textSecondary'} icon={def.enabled ? 'checkmark-circle-outline' : 'pause-circle-outline'}>{def.enabled ? 'Enabled' : 'Disabled'}</Chip>}
                                {!def.enabled && !archived && (
                                    <Text style={styles.catchupNote}>Enabling counts existing history</Text>
                                )}
                            </View>
                        </View>
                        <View style={styles.rowActions}>
                            {editable && !archived && (
                                <View
                                    onPointerDown={(e) => e?.stopPropagation?.()}
                                    onTouchEnd={(e) => e?.stopPropagation?.()}
                                >
                                    <Toggle
                                        testID={`milestone-enabled-${def.id}`}
                                        value={def.enabled === true}
                                        disabled={pendingToggleID === def.id}
                                        onValueChange={(next) => onToggleEnabled?.(def, next)}
                                    />
                                </View>
                            )}
                        </View>
                    </Pressable>
                );
            })}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, shadows }) => ({
    loading: {
        paddingVertical: spacing.xl,
        alignItems: 'center',
    },
    list: {
        gap: spacing.sm,
    },
    row: (archived) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.md,
        boxShadow: shadows.card,
        opacity: archived ? 0.75 : 1,
        _web: {
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease',
            _hover: { boxShadow: shadows.cardHover },
        },
    }),
    rowBody: {
        flex: 1,
        minWidth: 0,
        gap: 3,
    },
    title: (archived) => ({
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: 15,
        flexShrink: 1,
        textDecorationLine: archived ? 'line-through' : 'none',
    }),
    subtitle: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
    chipRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        marginTop: 2,
        flexWrap: 'wrap',
    },
    catchupNote: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: 11,
        fontStyle: 'italic',
    },
    rowActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
}));

export default MilestoneDefinitionList;
